/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   7/16/21
 */

import React, {useMemo} from "react";
import {Button, Collapse, Stack} from "@mui/material";
import {useSelector} from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ChevronRight";
import Row from "./Row";
import {State} from "../state/store";
import {
	VegaEvent,
	Employee,
	EmployeesGroupBySetting,
	GoogleEvent,
	Holiday,
	WorkdayEvent,
	OpsGenieEvent
} from "../interfaces/Global";
import {getEmployeeGroups, getGroupTitles} from "./content-groups";
import {contentSx} from "./sx";
import Footer from "./Footer";
import NowLine from "./NowLine";
import {addDays, areIntervalsOverlapping, startOfToday} from "date-fns";
import Header from "./Header";
import eachDay from "date-fns/eachDayOfInterval";

export interface BodyProps {
	range: Interval
	intervals: Date[]
	employees: Employee[]
	vegaEvents: VegaEvent[]
	googleEvents: GoogleEvent[]
	workdayEvents: WorkdayEvent[]
	opsGenieEvents: OpsGenieEvent[]
	intervalWidth: number
	intervalDuration: number
	rangeResolution: number
	normalizeEventsPosition: boolean
	capacityCalculateResolution: number
	oooEvents: Record<string, Interval[]>
	holidays: Holiday[]
}

export default function Body(props: BodyProps) {

	const groupBy = useSelector((s:State) => s.settings.groupBy) || "off";

	return (
		<Stack>
			{groupBy === "off" && <Content {...props} />}
			{groupBy !== "off" && <ContentGroups {...props} />}
		</Stack>
	);

}

function ContentGroups({ employees, ...props }: BodyProps & { employees: Employee[] }) {

	const filterTags = useSelector((s:State) => s.settings.filterTags || []);
	const groupBy = useSelector((s:State) => s.settings.groupBy) as Exclude<EmployeesGroupBySetting, "off">;

	let groupTitles: string[] = useMemo(
		() => getGroupTitles(employees, groupBy, filterTags).sort((a,b) => a.localeCompare(b)),
		[employees, groupBy, filterTags]
	);

	let groups: Record<string, Employee[]> = useMemo(
		() => getEmployeeGroups(groupTitles, employees, groupBy),
		[groupTitles, employees, groupBy]
	);

	return (
		<Stack>
			{Object.keys(groups).map(title =>
				<Group
					key={title}
					{...props}
					employees={groups[title]}
					title={title}
				/>
			)}
		</Stack>
	);

}

function Group({ title, ...props}: BodyProps & { employees: Employee[], title: string }) {

	const [collapsed, setCollapsed] = React.useState(false);
	const range = useSelector((s:State) => s.range);
	const days = useMemo(() => eachDay({ start: range.start, end: addDays(range.end, -1) }), [range]);
	const groupHeaderEnabled = useSelector((s:State) => s.settings.groupHeaderEnabled);
	const showBottomHeader = useSelector((s:State) => s.settings.showBottomHeader);
	const groupCapacityEnabled = useSelector((s:State) => s.settings.groupCapacityEnabled);

	return (
		<Stack sx={{
			width: "fit-content",
			mb: 1
		}}>
			<Button
				component="div"
				variant="outlined"
				fullWidth
				onClick={() => setCollapsed(!collapsed)}
				sx={{
					borderColor: "divider",
					width: "100%",
					position: "relative",
					justifyContent: "flex-start",
					zIndex: 200,
				}}
			>
				<Stack
					direction={"row"}
					alignItems={"center"}
					sx={{
						position: "sticky",
						display: "flex",
						left: 20
					}}
				>
					{!collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
					{title + " (" + props.employees.length + ")"}
				</Stack>
			</Button>
			<Collapse in={!collapsed} timeout="auto">
				{groupHeaderEnabled &&
					<Header
						intervals={days}
						intervalWidth={40}
						rangeResolution={40/1440}
						formats={[['MMM', 'd'], 'E']}
					/>}
				<Content {...props} />
				{groupHeaderEnabled && showBottomHeader &&
					<Header
						intervals={days}
						intervalWidth={40}
						rangeResolution={40/1440}
						formats={[['MMM', 'd'], 'E']}
					/>}
				{groupCapacityEnabled &&
				<Footer
					intervals={props.intervals}
					employees={props.employees}
					vegaEvents={props.vegaEvents}
					googleEvents={props.googleEvents}
					workdayEvents={props.workdayEvents}
					opsGenieEvents={props.opsGenieEvents}
					oooEvents={props.oooEvents}
					holidays={props.holidays}
					intervalWidth={props.intervalWidth}
					intervalDuration={props.intervalDuration}
					capacityCalculateResolution={props.capacityCalculateResolution}
				/>}
			</Collapse>
		</Stack>
	);

}

function Content(props: BodyProps & { employees: Employee[] }) {

	return (
		<Stack sx={contentSx}>
			{props.employees.map(employee =>
				<Row
					key={employee.id}
					range={props.range}
					employee={employee}
					vegaEvents={props.vegaEvents.filter(e => e.employeeId === employee.id)}
					googleEvents={props.googleEvents.filter(e => e.employeeId === employee.id)}
					workdayEvents={props.workdayEvents.filter(e => e.employeeId === employee.id)}
					opsGenieEvents={props.opsGenieEvents.filter(e => e.employeeId === employee.id)}
					oooEvents={props.oooEvents[employee.id] || []}
					holidays={props.holidays.filter(h => h.countryCode === employee.countryCode && (!h.state || h.state === employee.state))}
					intervals={props.intervals}
					intervalWidth={props.intervalWidth}
					intervalDuration={props.intervalDuration}
					rangeResolution={props.rangeResolution}
					normalizeEventsPosition={props.normalizeEventsPosition}
				/>)}
			{areIntervalsOverlapping(props.range, { start: startOfToday(), end: addDays(startOfToday(), 1)})
				&& props.rangeResolution > 1 &&
			<NowLine
				key={props.rangeResolution}
				range={props.range}
				rangeResolution={props.rangeResolution}
			/>}
		</Stack>
	);
}